import { useCallback, useEffect, useState } from "react";
import type { Plan } from "../../../shared/goals/plan";
import { useNative } from "../../../shared/phone/media";
import { GrowText } from "../../components/ui/GrowText";
import { Inline, Rich } from "../../components/ui/Rich";

/** The calls the owner marked "Not right", with what they wrote: empty until they type. */
export type PlanNotes = Record<string, string>;

const keyOf = (goalId: string, version: number) => `fz.plan-notes.${goalId}.${version}`;

function read(key: string): PlanNotes {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "{}") as PlanNotes;
  } catch {
    return {};
  }
}

function write(key: string, notes: PlanNotes) {
  try {
    if (Object.keys(notes).length) localStorage.setItem(key, JSON.stringify(notes));
    else localStorage.removeItem(key);
  } catch {
    return;
  }
}

/**
 * The owner's "Not right" marks on one plan version. Why: they stay on this device until "Plan again with your
 * changes" sends them, so a mark taken back never reaches the agent.
 */
export function usePlanNotes(goalId: string, version: number | null) {
  const key = version === null ? null : keyOf(goalId, version);
  const [notes, setNotes] = useState<PlanNotes>(() => (key ? read(key) : {}));
  useEffect(() => setNotes(key ? read(key) : {}), [key]);
  /** `null` takes the mark back; a string (empty at first) marks the call and holds what is right. */
  const set = useCallback(
    (call: string, text: string | null) => {
      if (!key) return;
      setNotes((n) => {
        const next = { ...n };
        if (text === null) delete next[call];
        else next[call] = text;
        write(key, next);
        return next;
      });
    },
    [key],
  );
  return { notes, set };
}

/** The marks with words, which are what a new plan can act on. */
export const written = (notes: PlanNotes) => Object.entries(notes).filter(([, t]) => t.trim());

/** The section a call's words are sent under, so the agent knows which call they answer. */
export const callSection = (call: string) => `What it decided: ${call}`;

/** What the plan decided, each call standing until the owner says "Not right" (the product profile's pattern). */
export function PlanCalls({ plan, locked, notes, onSet }: { plan: Plan; locked: boolean; notes: PlanNotes; onSet: (call: string, text: string | null) => void }) {
  const native = useNative();
  if (!plan.choices.length) return null;
  if (native)
    return (
      <section>
        <div className="pn-cap">What it decided</div>
        <div className="pn-grp">
          {plan.choices.map((c) => {
            const mine = notes[c.choice] !== undefined;
            return (
              <div className={`pn-row static su-call${mine ? " mine" : ""}`} key={c.choice}>
                <span className="m">
                  {mine ? (
                    <>
                      <GrowText className="pn-inp su-callta" rows={2} aria-label="What is right" placeholder="What is right?" autoFocus value={notes[c.choice]} onChange={(e) => onSet(c.choice, e.target.value)} />
                      <span className="s su-wrap">
                        <b>It said</b> {c.choice}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="t su-wrap">{c.choice}</span>
                      <span className="s su-wrap">
                        <Inline text={c.why} />
                      </span>
                    </>
                  )}
                </span>
                {!locked && (
                  <button type="button" className={`su-nr${mine ? " on" : ""}`} aria-pressed={mine} onClick={() => onSet(c.choice, mine ? null : "")}>
                    {mine ? "Keep it" : "Not right"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  return (
    <section className="card psec" id="p-calls">
      <h3>What it decided</h3>
      {plan.choices.map((c) => {
        const mine = notes[c.choice] !== undefined;
        return (
          <div className={mine ? "wf-row mine" : "wf-row"} key={c.choice}>
            {mine ? (
              <div className="wf-mine">
                <GrowText className="wf-text" rows={1} aria-label="What is right" placeholder="What is right?" autoFocus value={notes[c.choice]} onChange={(e) => onSet(c.choice, e.target.value)} />
                <p className="wf-was">
                  <b>It said</b>
                  {c.choice}
                </p>
              </div>
            ) : (
              <div>
                <b className="wf-h">{c.choice}</b>
                <Rich block className="wf-b rich pl-p" text={c.why} />
              </div>
            )}
            {!locked && (
              <button type="button" className="wf-nr" aria-pressed={mine} onClick={() => onSet(c.choice, mine ? null : "")}>
                Not right
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
}
